import React, { useEffect, useRef } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import './ConfirmationModal.css';

export interface ConfirmationModalProps {
  /** Whether the modal is visible */
  isOpen: boolean;
  /** Modal title */
  title: string;
  /** Modal message/description */
  message: string;
  /** Confirm button label */
  confirmLabel?: string;
  /** Cancel button label */
  cancelLabel?: string;
  /** Handler when confirm is clicked */
  onConfirm: () => void;
  /** Handler when cancel is clicked or modal is dismissed */
  onCancel: () => void;
  /** Whether this is a destructive/dangerous action */
  danger?: boolean;
  /** Whether the confirm button is in loading state */
  loading?: boolean;
  /** Optional class name */
  className?: string;
}

/**
 * Confirmation modal for user actions requiring explicit confirmation.
 * Used for delete operations, batch actions, and other important decisions.
 */
export const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  onConfirm,
  onCancel,
  danger = false,
  loading = false,
  className = '',
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const confirmButtonRef = useRef<HTMLButtonElement>(null);

  // Focus management and keyboard handling
  useEffect(() => {
    if (!isOpen) return;

    // Focus the confirm button when modal opens
    confirmButtonRef.current?.focus();

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCancel();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onCancel]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onCancel();
    }
  };

  return (
    <div
      className={`confirmation-modal-backdrop ${className}`}
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirmation-modal-title"
      aria-describedby="confirmation-modal-message"
    >
      <div className="confirmation-modal" ref={modalRef}>
        <div className="confirmation-modal-header">
          {danger && (
            <div className="confirmation-modal-icon confirmation-modal-icon--danger">
              <AlertTriangle className="confirmation-modal-icon-svg" />
            </div>
          )}
          <h2 id="confirmation-modal-title" className="confirmation-modal-title">
            {title}
          </h2>
          <button
            type="button"
            className="confirmation-modal-close"
            onClick={onCancel}
            aria-label="Close modal"
          >
            <X className="confirmation-modal-close-icon" />
          </button>
        </div>

        <p id="confirmation-modal-message" className="confirmation-modal-message">
          {message}
        </p>

        <div className="confirmation-modal-actions">
          <button
            type="button"
            className="confirmation-modal-button confirmation-modal-button--cancel"
            onClick={onCancel}
            disabled={loading}
          >
            {cancelLabel}
          </button>
          <button
            ref={confirmButtonRef}
            type="button"
            className={`confirmation-modal-button confirmation-modal-button--confirm ${
              danger ? 'confirmation-modal-button--danger' : ''
            }`}
            onClick={onConfirm}
            disabled={loading}
          >
            {loading ? 'Loading...' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationModal;
